import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';

const REVIEW_URL = 'https://whop.com/stockmethodllc/reviews/';
// Shows once, ~1.5 minutes after a member lands in the dashboard.
const DELAY_MS = 90000;
const keyFor = (uid) => `sm_review_prompt_v1_${uid}`;

export function ReviewPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const uid = user?.user_id;
    if (!uid) return;
    const key = keyFor(uid);
    if (localStorage.getItem(key)) return; // already seen — never show again
    const t = setTimeout(() => {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, String(Date.now())); // mark seen on first appearance
        setOpen(true);
      }
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [user?.user_id]);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', duration: 0.35 }}
            className="relative z-10 w-full max-w-sm rounded-3xl border border-emerald/30 bg-ink-card p-8 text-center shadow-inner_glow"
            data-testid="review-prompt-modal"
          >
            <button onClick={close} className="absolute right-4 top-4 text-zinc-500 hover:text-white" data-testid="review-prompt-close">
              <X size={20} />
            </button>
            <div className="mx-auto flex items-center justify-center gap-1 text-emerald">
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <Star size={26} className="fill-emerald text-emerald" />
                </motion.span>
              ))}
            </div>
            <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">
              Enjoying StockMethod?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              It takes 30 seconds and genuinely helps the community grow. Drop us a quick review —
              we'd really appreciate it. 🙏
            </p>
            <div className="mt-8 space-y-3">
              <a
                href={REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                data-testid="review-prompt-leave"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
              >
                Leave a review <ArrowUpRight size={16} />
              </a>
              <Button pill size="md" variant="outline" className="w-full" onClick={close} data-testid="review-prompt-later">
                Maybe later
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
